// Feeling-tuners for the Memory Bloom.
//
// AGE is the hero control — the living-memory metaphor. Pull it to the left and
// you see the organism as it was in its first month (a tiny, single-tone
// sprout); pull it right and you see two years of accreted memory (a sprawling,
// multi-hue, feathered network). Every other macro shapes *how* it grows.
//
//   AGE        — memory age. drives generations, reach, color spread, blur.
//   SPREAD     — how far the organism reaches from the origin.
//   DENSITY    — how many shapes pack into each generation.
//   ROUGHNESS  — how wild/organic the lobe outlines are.
//   BLEED      — how watery the dye is — crisp edges ←→ feathered chromatography.
//   CORE       — strength & size of the central origin bloom.
//   NETWORK    — connecting filaments between lobes (islands ←→ web).
//   BREATH     — speed of the living motion.

import type { BloomParams } from "./bloom";
import type { DerivedPalette, RGB } from "./color";
import type { BloomRenderState } from "./bloomRenderer";

export type MacroKey =
  | "age"
  | "spread"
  | "density"
  | "roughness"
  | "bleed"
  | "core"
  | "network"
  | "breath"
  // structural (trait-driven, no default slider)
  | "roundness"
  | "form"
  // atmosphere / post-processing
  | "shimmer"
  | "glow"
  | "grain"
  | "gravity"
  // cellular morphology (read by the GPU renderer; 0 = solid lobes)
  | "holes"
  // WARM COIL variant (ported from the blob visualizer). All gated by `warm`:
  // at warm=0 the renderer is the exact flower — these are inert. No slider UI.
  | "warm"
  | "spiralTightness"
  | "petalBulb"
  | "petalCadence"
  | "glass"
  | "innerLight"
  | "veinGlow"
  | "veinDensity"
  | "veinOpacity"
  | "particles";

export const MACRO_DEFS: Array<{ key: MacroKey; label: string; hint: string }> = [
  { key: "age",       label: "AGE",       hint: "new ←→ years of memory" },
  { key: "spread",    label: "SPREAD",    hint: "compact ←→ far-reaching" },
  { key: "density",   label: "DENSITY",   hint: "sparse ←→ dense" },
  { key: "roughness", label: "ROUGHNESS", hint: "smooth ←→ wild edges" },
  { key: "bleed",     label: "BLEED",     hint: "crisp ←→ watery bloom" },
  { key: "core",      label: "CORE",      hint: "faint ←→ strong center" },
  { key: "network",   label: "NETWORK",   hint: "islands ←→ connected web" },
  { key: "breath",    label: "BREATH",    hint: "still ←→ alive" },
];

// Atmosphere / post-processing controls — the ethereal, hypnotic finish.
export const ATMOSPHERE_DEFS: Array<{ key: MacroKey; label: string; hint: string }> = [
  { key: "shimmer", label: "SHIMMER", hint: "still hue ←→ hallucinating" },
  { key: "glow",    label: "BLOOM",   hint: "matte ←→ radiant bloom" },
  { key: "grain",   label: "GRAIN",   hint: "clean ←→ filmic" },
  { key: "gravity", label: "TOUCH",   hint: "still ←→ leaves part at cursor" },
];

export type MacroState = Record<MacroKey, number>;

export const MACRO_DEFAULTS: MacroState = {
  age: 0.55,
  spread: 0.6,
  density: 0.5,
  roughness: 0.45,
  // BLEED HIGH by default (Task 6, ~0.9) — every organism reads as a soft
  // watercolour bloom with feathered, melting edges (not crisp).
  bleed: 0.9,
  core: 0.6,
  network: 0.4,
  breath: 0.5,
  roundness: 0.55,
  form: 0.25,
  // atmosphere — shimmer for life, but HDR BLOOM (glow) and film GRAIN are OFF:
  // the bright-pass bloom + procedural grain read as a noisy glow inside the
  // leaves, which we don't want. Clean watercolour wash instead.
  shimmer: 0.65,
  glow: 0,
  grain: 0,
  gravity: 0.6,
  holes: 0,
  // WARM COIL — OFF by default (warm:0 → renderer is the exact flower). The rest are
  // the blob's dialed-in warm values, inert until a mount sets warm:1.
  warm: 0,
  spiralTightness: 0.76,
  petalBulb: 0.02,
  petalCadence: 0.44,
  glass: 0.37,
  innerLight: 0.55,
  veinGlow: 0.65,
  veinDensity: 0.57,
  veinOpacity: 0.47,
  particles: 0.55,
};

// Memory → organism age, on a TWO-SEGMENT curve so the first commits are felt.
//
//  • PRESENCE: a brand-new organism already has body (FLOOR 0.30) — no
//    barely-there sprout. Even zero memory reads as a small living bloom.
//  • FAST EARLY GROWTH: a steep first segment over the first ~SPRINT nodes
//    (≈20). One or two commits visibly grow it — you watch it bloom live.
//  • SLOW MATURITY: past the sprint it crosses smoothly into the original slow
//    saturating curve, so years of memory are still earned, not given.
//
// Reference values: 0→0.30, 1≈0.32, 2≈0.35, 5≈0.41, 20≈0.55, 100≈0.60,
// 500≈0.68, 1500≈0.78. The big visible jumps live in the first dozen commits.
//
// The input is the user's CELLS (lib/usage/cells.ts) — nodes + dimensions +
// current self_facts — the truthful total memory, not a nodes-only count.
export function ageFromMemory(nodeCount: number): number {
  // A newborn used to open at 0.30, which is a real bloom but a THIN one — few
  // enough petals that the centre cell read as the whole creature and the thing
  // you were introduced to looked unfinished. It now starts a little further
  // along: complete at a glance, with all of the growing still ahead of it.
  // (`sizeFromMemory` takes the other half of that trade — a young netti is
  // fuller but physically smaller, so it never overbears the room it's born in.)
  const FLOOR = 0.38;   // presence at birth — a small bloom, but a whole one
  const CEIL = 0.82;
  const SPRINT = 20;    // nodes over which the steep early segment plays out
  const SPRINT_TO = 0.55; // age reached by the end of the sprint (~20 nodes)
  const K = 1200;       // larger ⇒ slower to mature (the long tail)
  const n = Math.max(0, nodeCount);
  // segment 1 — steep ease-out rise from FLOOR to SPRINT_TO over [0, SPRINT]
  if (n <= SPRINT) {
    const t = n / SPRINT;
    const e = 1 - Math.pow(1 - t, 2.2); // fast off the line, settling toward SPRINT_TO
    return FLOOR + (SPRINT_TO - FLOOR) * e;
  }
  // segment 2 — the original slow saturating tail, anchored to continue from
  // SPRINT_TO at n=SPRINT so the two segments meet without a kink.
  const tail = 1 - Math.exp(-(n - SPRINT) / K);
  return SPRINT_TO + (CEIL - SPRINT_TO) * tail;
}

// ── HOW BIG netti is, as opposed to how grown ────────────────────────────────
// Age fills the body in — more petals, further out. This is the other axis: the
// creature's actual SIZE in the room, which starts modest and grows with the
// person. A newborn at full size is all centre and air; the same bloom at 0.86
// reads as a small, complete thing that has room to become larger.
//
// Deliberately a narrow band (0.86 → 1.0 across a whole lifetime of memory).
// This multiplies whatever scale a surface has already chosen, so it must never
// fight that surface's composition — it is a nudge, not a layout.
export function sizeFromMemory(age: number): number {
  const YOUNG = 0.38, OLD = 0.82;           // the age law's own floor + ceiling
  const t = Math.max(0, Math.min(1, (age - YOUNG) / (OLD - YOUNG)));
  return 0.86 + 0.14 * (1 - Math.pow(1 - t, 1.6)); // fast early, then settling
}

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
const ease = (t: number) => 1 - Math.pow(1 - t, 2.0);

export type BloomConfig = {
  params: Omit<BloomParams, "morphId">;
  reach: number;       // radial reach multiplier (× scale)
  intensity: number;   // overall pigment strength
  shimmer: number;     // 0..1 chromatic-aberration / hue-shift gain
  gravity: number;     // 0..1 cursor gravity strength
};

export function configFromMacros(m: MacroState): BloomConfig {
  return {
    params: {
      age: m.age,
      density: m.density,
      spread: m.spread,
      roughness: m.roughness,
      bleed: m.bleed,
      core: m.core,
      network: m.network,
      breath: m.breath,
      roundness: m.roundness,
      form: m.form,
      // WARM COIL geometry — the generator winds a nautilus coil when warm>0.5;
      // spiralTightness sets how tightly. Inert at warm:0 (flower generation runs).
      warm: m.warm,
      spiralTightness: m.spiralTightness,
      holes: m.holes,
    },
    reach: 0.55 + ease(m.spread) * 0.7,
    intensity: clamp(0.78 + m.density * 0.12, 0, 1),
    shimmer: m.shimmer,
    gravity: m.gravity,
  };
}

export function buildRenderState(
  cfg: BloomConfig,
  palette: DerivedPalette,
  bg: RGB,
  scale: number,
  morphId: number,
  pointer: { x: number; y: number } | null,
  zoom: number,
  pan: [number, number],
): BloomRenderState {
  return {
    palette,
    bg,
    scale,
    age: cfg.params.age,
    reach: cfg.reach,
    bleed: cfg.params.bleed,
    core: cfg.params.core,
    network: cfg.params.network,
    roughness: cfg.params.roughness,
    intensity: cfg.intensity,
    morphId,
    shimmer: cfg.shimmer,
    gravity: cfg.gravity,
    pointer,
    zoom,
    pan,
    rotation: 0,
    spinByGen: null,
    // optional layers — defaults; bloomField attaches focus/streams before rendering
    focus: null,
    streams: null,
    getLobePos: null,
    poolAlpha: 1,
  };
}
